import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createEventProgramItem,
  deleteEventProgramItem,
  getEventProgramItems,
  updateEventProgramItem,
} from "@/lib/api/clients/EventProgramClient";
import { EventProgramItem } from "@/types/EventProgramItem";
import { sortProgramItemsChronologically } from "@/lib/utils/programOrder";

/**
 * Sorted copy in the display order — CHRONOLOGICAL (`startTime`), with
 * `sortIndex` demoted to a silent tiebreak. See lib/utils/programOrder.ts for
 * the full order and why it lives in its own module.
 *
 * Every consumer of `useEventProgram` inherits this, which is the point: the
 * cockpit list, the website bake's input and the editor's SessionGroup preview
 * used to be able to disagree with the attendee app and the session mails.
 */
export function sortProgramItems(
  items: readonly EventProgramItem[],
): EventProgramItem[] {
  return sortProgramItemsChronologically(items);
}

export function useEventProgram(eventId: string | null | undefined) {
  return useQuery({
    queryKey: ["eventProgram", eventId],
    queryFn: async () => sortProgramItems(await getEventProgramItems(eventId!)),
    enabled: !!eventId,
    staleTime: 15_000,
  });
}

/** Occupancy freshness (audit: badge stale until reload): every program-item
 *  create/update/delete can change what the occupancy endpoint reports for
 *  OFFERING blocks (bind/unbind, capacity, server-side downgrades), so the
 *  timeline's badge query goes stale together with the list. Non-OFFERING
 *  writes over-invalidate harmlessly — the query only refetches where a
 *  timeline is mounted, and kind isn't reliably readable from the mutation
 *  variables (delete only carries an id). */
function invalidateProgramOccupancy(
  queryClient: ReturnType<typeof useQueryClient>,
  eventId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: ["programOccupancy", eventId],
  });
}

export function useCreateEventProgramItem(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (item: EventProgramItem) =>
      createEventProgramItem(eventId, item),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["eventProgram", eventId],
      });
      invalidateProgramOccupancy(queryClient, eventId);
    },
  });
}

export function useUpdateEventProgramItem(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      programItemId,
      patch,
      clearEndTime,
      clearCapacity,
      clearProgrammeId,
      clearRoomId,
      clearOfferingId,
    }: {
      programItemId: string;
      patch: Partial<EventProgramItem>;
      /** Explicit unset of the optional endTime (absent ≠ clear on the wire). */
      clearEndTime?: boolean;
      /** Explicit unset of the optional capacity (back to unlimited). */
      clearCapacity?: boolean;
      /** Explicit unset of the programme assignment (back to unassigned). */
      clearProgrammeId?: boolean;
      /** Explicit unset of the seating-room link. */
      clearRoomId?: boolean;
      /** Explicit unset of the 1:1 offering-container link (backend rejects
       *  on OFFERING — kind is API-immutable). */
      clearOfferingId?: boolean;
    }) =>
      updateEventProgramItem(
        eventId,
        programItemId,
        patch,
        clearEndTime,
        clearCapacity,
        clearProgrammeId,
        clearRoomId,
        clearOfferingId,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["eventProgram", eventId],
      });
      invalidateProgramOccupancy(queryClient, eventId);
    },
  });
}

/**
 * Time-move of one item (timeline drag/resize): the same PUT as
 * useUpdateEventProgramItem, but OPTIMISTIC — the cached list is patched
 * before the request so a dragged block doesn't snap back while the write is
 * in flight. Rolls back to the snapshot on error (e.g. 409
 * offeringWindowShrinkBlocked) and re-syncs with the server either way.
 */
export function useMoveEventProgramItem(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      programItemId,
      startTime,
      endTime,
      programmeId,
    }: {
      programItemId: string;
      /** New ISO start instant. */
      startTime: string;
      /** New ISO end instant — omit for open-ended items (a move must not
       *  invent an end out of thin air). */
      endTime?: string;
      /** Lane-drop track reassign (timeline lanes): new track id — travels
       *  in the same PUT as the time patch. Omit = track untouched. A drag
       *  never CLEARS a track (Welle C: the "No track" safety lane is no
       *  drop target), so there is no clear flag here. */
      programmeId?: string;
    }) => {
      const patch = {
        startTime,
        ...(endTime !== undefined ? { endTime } : {}),
        ...(programmeId ? { programmeId } : {}),
      };
      return updateEventProgramItem(eventId, programItemId, patch);
    },
    onMutate: async ({
      programItemId,
      startTime,
      endTime,
      programmeId,
    }) => {
      // Cancel in-flight refetches so a stale response can't overwrite the
      // optimistic patch between now and settle.
      await queryClient.cancelQueries({ queryKey: ["eventProgram", eventId] });
      const previous = queryClient.getQueryData<EventProgramItem[]>([
        "eventProgram",
        eventId,
      ]);
      if (previous) {
        queryClient.setQueryData<EventProgramItem[]>(
          ["eventProgram", eventId],
          // RE-SORT, don't just map: `startTime` IS the sort key now, so a move
          // can change the item's position. A plain map keeps the old array
          // order and hands a wrongly ordered list to everything reading this
          // cache — the timeline, the cockpit list and the website bake — until
          // onSettled's refetch corrects it.
          sortProgramItemsChronologically(
            previous.map((item) => {
              if (item.programItemId !== programItemId) return item;
              return {
                ...item,
                startTime,
                ...(endTime !== undefined ? { endTime } : {}),
                ...(programmeId ? { programmeId } : {}),
              };
            }),
          ),
        );
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["eventProgram", eventId], context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["eventProgram", eventId],
      });
    },
  });
}

export function useDeleteEventProgramItem(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (programItemId: string) =>
      deleteEventProgramItem(eventId, programItemId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["eventProgram", eventId],
      });
      invalidateProgramOccupancy(queryClient, eventId);
    },
  });
}
